import { Injectable, OnDestroy } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import * as sdk from "matrix-js-sdk";
import {
    ClientEvent,
    MatrixClient,
    RoomEvent,
    MatrixEvent,
    EventType,
    MatrixError,
    IEventWithRoomId,
    RoomMember,
    Room,
    Preset,
    Visibility
} from "matrix-js-sdk/lib/matrix";
import EventEmitter from "events";
import * as gen from "random-seed";

import { Color } from "../_objects/color.object";
import { Message } from "../_objects/message";
import { API, MATRIX, MATRIX_URL } from "../_constants/constants";
import { AppAuth, AuthService, HackathonUser, MatrixUser } from "./auth.service";
import { SystemService } from "./system.service";
import { AlertController, ModalController, NavController, ToastController } from "@ionic/angular";
import { Router } from "@angular/router";
import { NewMessageComponent } from "../_modals/new-message/new-message.component";
import { logger } from "matrix-js-sdk/lib/logger";
import { Subscription } from "rxjs";
import { Reaction } from "../_objects/reaction";

// If you don't need this logic anymore, remove it:
const { nameToUint64 } = require("eosjs-account-name");

export const GeneralChatRoom = "myltSKQpavQKrxfQQN";
export const GeneralChatRoomId = `!${GeneralChatRoom}:${MATRIX}`;
export const SupportUser = "wire";
export const SupportUserId = `@${SupportUser}:${MATRIX}`;

export const DEFAULT_PFP = 'assets/img/personbg.png'

interface ProfileInfo {
    profile?: string;  // avatar URL
    color: string;
}

export interface ToastData {
    header: string;
    message?: string;
    icon?: string;
    link?: string;
    linkText?: string;
    duration?: number;
    color?: string;
    route?: string;
    roomId?: string;
}

@Injectable({
    providedIn: "root"
})
export class MatrixService extends EventEmitter implements OnDestroy {
    client: sdk.MatrixClient | undefined;

    rooms: sdk.Room[] = [];
    // publicRooms: sdk.IPublicRoomsChunkRoom[] = [];
    // directs: Room[] = [];

    get allDirects(){
        return this.rooms.filter(room => room.getMyMembership() === 'join' && room.getJoinRule() !== 'public');
    }
    get directs(){
        return this.allDirects.filter(room => !this.isSupportRoom(room));
    }
    get supportRooms(){
        return this.allDirects.filter(room => this.isSupportRoom(room));
    }
    get supportRoom(){
        return this.supportRooms.length > 0 ? this.supportRooms[0] : null;
    }
    get invites(){
        return this.rooms.filter(room => room.getMyMembership() === 'invite');
    }
    get publicRooms(){
        return this.rooms.filter(room => room.getJoinRule() === 'public');
    }


    idRooms: Map<string, sdk.Room> = new Map();
    roomMessages: Map<string, Message[]> = new Map();
    profiles: Map<string, ProfileInfo> = new Map();
    events: Map<string, Partial<MatrixEvent["event"]>> = new Map();
    reations: Map<string, Map<string, Reaction>> = new Map();

    clientReady = false;
    roomsLoaded = false;
    lastMessages: Map<string, any> = new Map();
    retryCount = 4;

    authChange?: Subscription;

    constructor(
        private auth: AuthService,
        private http: HttpClient,
        private nav: NavController,
        private toast: ToastController,
        private router: Router,
        private system: SystemService,
        private modal: ModalController,
        private alert : AlertController
    ) {
        super();
        // Create a default client pointing to your matrix server
        this.client = sdk.createClient({
            baseUrl: MATRIX_URL
        });

        this.authChange = this.auth.currentAuth$.subscribe((user) => {
            console.log("Auth Change:", user);
            
            if (user) {
                this.init(user);
            } else {
                this.clearData();
            }
        });
    }

    ngOnDestroy() {
        this.authChange?.unsubscribe();
    }

    /**
     * Initialize the Matrix client by calling your local API endpoint
     * to fetch user_id, access_token, etc. Then start the sync process.
     */
    init(auth : AppAuth): Promise<MatrixClient> {
        return new Promise<MatrixClient>(async (resolve, reject) => {
            logger.disableAll(); // disable matrix debug logs (optional)

            if (!auth.user) {
                return reject("No logged in user or missing ID; cannot init matrix.");
            }
            if (!this.auth.authHeader) {
                return reject("No auth header set; cannot init matrix.");
            }

            let matrixAuth = auth.matrix;
            if (!matrixAuth) {
                // Attempt server-side login/registration for matrix
                matrixAuth = await this.matrixLogin().catch((err) => {
                    throw new Error("Matrix login failed");
                });
                console.log("Retrieved Matrix Auth", matrixAuth);
            }

            // Initialize the client with the retrieved auth
            this.client = sdk.createClient({
                baseUrl: MATRIX_URL,
                accessToken: matrixAuth.access_token,
                userId: matrixAuth.user_id,
                deviceId: matrixAuth.device_id
            });

            // Set up event listeners
            this.setupMatrixListeners();

            // Start the sync process
            await this.client.startClient({ initialSyncLimit: 10 });
            this.clientReady = true;
            resolve(this.client);
        });
    }

    matrixLogin(): Promise<MatrixUser> {
        return new Promise((resolve, reject) => {
            console.log("Logging into Matrix...", this.auth.authHeader);
            
            this.http.post<MatrixUser>(`${API}/matrix-login`, {}, this.auth.authHeader).subscribe(
                async (result) => {
                    resolve(result);
                },
                async (err) => {
                    const t = await this.toast.create({
                        header: "Messenger Login Failed",
                        duration: 6000,
                        position: "bottom",
                        color: "danger",
                        cssClass: "my-toast",
                        // buttons: [
                        //     {
                        //         text: "Retry",
                        //         handler: () => {
                        //             this.init();
                        //         }
                        //     }
                        // ]
                    });
                    t.present();
                    reject(err);
                }
            );
        });
    }

    /**
     * Set up various Matrix client event listeners
     */
    private setupMatrixListeners() {
        if (!this.client) return;

        this.client.once(ClientEvent.Sync, async (state) => {
            if (state === "PREPARED") {
                await this.populateRooms();
                this.roomsLoaded = true;
                console.log("ROOMS LOADED");
                
                // if (!this.rooms.some(room => room.roomId === GeneralChatRoomId)) {
                //     console.log("Joining General Chat Room...");
                //     try {
                //         const room = await this.client.joinRoom(GeneralChatRoomId);
                //         console.log("Joined General Chat Room", room);
                //         await this.populateRooms()
                //         // this.emit("refresh-rooms");
                //     } catch (joinErr) {
                //         console.log(`Could not join ${GeneralChatRoomId}`, joinErr);
                //     }
                // }
                // else {
                //     this.emit("rooms-ready");
                //     console.log("Rooms Ready", this.rooms);
                // }
                // if (!this.rooms.some(room => room.roomId === SupportUsername)) {
                // this.createRoom(SupportUser).then((roomId) => {
                //     console.log("Support Room Created:", roomId);
                // })
 
            }
        });

        this.client.on(RoomEvent.Timeline, (event, room, toStartOfTimeline) => {
            let type = event.getType();
            // if (room?.roomId == "!ppiFCnUdTDItYyFjmN:matrix.org")
            //     console.log("Room Event Timeline: ", room?.roomId, type, event, event.event.content);

            switch (type) {
                case EventType.RoomMessage:
                    let msgs = this.roomMessages.get(room!.roomId);
                    let msg = new Message(this, event);

                    if (msgs) if (msgs[0].time > msg.time) msgs.push(msg); else msgs.unshift(msg);
                    else this.roomMessages.set(room!.roomId, [msg]);

                    break;
                case EventType.Reaction:
                    // console.log('REACTION', event);
                    let content = event.event.content;
                    if (!content) return;
                    let relation = content['m.relates_to'];
                    if (!relation || !relation.event_id) return;
                    let code = relation.key!;
                    let emojis = this.reations.get(relation.event_id);
                    if (!emojis) {
                        emojis = new Map<string, Reaction>();
                        emojis.set(code, new Reaction([event]));
                        this.reations.set(relation.event_id, emojis);
                    } else {
                        let emoji = emojis.get(code);
                        if (emoji) emoji.addEvent(event);
                        else emojis.set(code, new Reaction([event]));
                    }
                    // console.log(emojis);

                    // if(reactions) reactions.push(event);
                    // else this.reations.set(relation.event_id, [event]);
                    break;

                case EventType.RoomMember:
                    // console.log('ROOM RoomMember', event);
                    let msgs2 = this.roomMessages.get(room!.roomId);
                    let msg2 = new Message(this, event);

                    if (msgs2) if (msgs2[0].time > msg2.time) msgs2.push(msg2); else msgs2.unshift(msg2);
                    else this.roomMessages.set(room!.roomId, [msg2]);
                    // this.populateRooms();
                    break;

                case EventType.RoomCreate:
                    // console.log('ROOM RoomCreate', event);
                    this.populateRooms();
                    break;
                default:
                    // console.log('UNHANDLED EVENT', type, event.event);
                    break;
            }

            if (event.event.event_id) {
                this.events.set(event.event.event_id, event.event);
            }
        })
    }

    /**
     * Get all rooms for the logged-in user
     */
    async populateRooms(broadcast?: boolean): Promise<sdk.Room[]> {
        // console.log("Populating rooms...");
        
        if (!this.client) return;

        this.rooms = await this.client.getRooms()

        // const res = await this.client.publicRooms();
        // console.log("all Public Rooms:", res.chunk);
        // this.rooms = res.chunk
        // this.publicRooms = res.chunk.filter(r => r.room_id === GeneralChatRoomId);
        // console.log("Public Rooms:", this.publicRooms);
        // console.log("All Rooms:", this.rooms);
        // console.log("Rooms:", this.rooms);
        const isInGeneralRoom = this.rooms.some(room => room.roomId === GeneralChatRoomId);
        // console.log("In General Room:", isInGeneralRoom);
        if (!isInGeneralRoom) {
            // console.log("Joining General Chat Room...");
            try {
                const room = await this.client.joinRoom(GeneralChatRoomId);
                // console.log("Joined General Chat Room", room);
                await this.populateRooms()
                // this.emit("refresh-rooms");
            } catch (joinErr) {
                console.log(`Could not join ${GeneralChatRoomId}`, joinErr);
            }
        }

        // const isInSupportRoom = this.isInSupportRoom();

        // const directMap = this.client
        //     ?.getAccountData("m.direct")
        //     ?.getContent() as { [userId: string]: string[] } || {};
        // console.log("Direct Map:", directMap);
        
        for (const room of this.rooms) {
            // console.log("Room:", room.roomId, room.getMyMembership());
            
            this.idRooms.set(room.roomId, room);

            switch (room.getMyMembership()) {
                case 'invite':
                    // Attempt to auto-join the invite:
                    this.onJoinRoom(room);
                    break;

                case 'join':
                    // Already joined; add to directs array:
                    // if (!this.directs.some(directRoom => directRoom.roomId === room.roomId)) {
                    //     this.directs.push(room);
                    // }
                    // this.directs.push(room);
                    break;
            }
        }
        if (broadcast) {
            this.emit("rooms-ready");
        }
        return this.rooms;
    }


    /**
     * Joins a given room (by room object or public room info). 
     * After join, repopulates the room lists.
     */
    async onJoinRoom(room: Room | sdk.IPublicRoomsChunkRoom) {
        const roomId = (room as Room).roomId || (room as sdk.IPublicRoomsChunkRoom).room_id;
        const joinedRoom = await this.client.joinRoom(roomId);

        if (joinedRoom.getMyMembership() === 'join') {
            this.refreshRooms()
            // After successful join, refresh all rooms:
            // this.populateRooms(this.matrix.client.getRooms());
        }
    }

    /**
     * Refresh the rooms by re-fetching them
     */
    refreshRooms(): Promise<Room[]> {
        return new Promise(async (resolve) => {
            if (!this.client) return resolve([]);
            this.roomsLoaded = false;
            await this.populateRooms()
            // this.rooms = await this.client.getRooms();
            // for (const room of this.rooms) {
            //     this.idRooms.set(room.roomId, room);
            // }
            this.roomsLoaded = true;
            resolve(this.rooms);
        });
    }

    isInSupportRoom(){
        return this.rooms.some(room => room.getJoinedMembers().some(member => {
            member.userId === SupportUserId && room.getJoinedMembers().length <= 2
        }))
    }

    async chatWithSupport(){
        if (!this.roomsLoaded) await this.populateRooms()        
        if (this.supportRoom) this.nav.navigateForward(['app', 'tabs', 'chat', 'room', this.supportRoom.roomId])
        else this.createRoom(SupportUser).then((roomId) => {
            console.log("Support Room Created:", roomId);
            this.nav.navigateForward(['app', 'tabs', 'chat', 'room', roomId])
        })
    }
    /**
     * Stop the Matrix client and clear local data
     */
    clearData() {
        this.roomsLoaded = false;
        this.rooms = [];
        this.idRooms.clear();
        this.roomMessages.clear();
        this.profiles.clear();
        this.events.clear();

        if (this.client) {
            this.client.stopClient();
            this.client = undefined;
        }
    }

    isSupportRoom(room: Room){
        const members = room.getJoinedMembers()
        if (members.length <= 2 && members.some(member => member.userId === SupportUserId)) return true
        else return false
    }

    members(room?: sdk.Room) {
        return room?.getMembers().filter(m => this.sliceId(m.userId) != this.auth.user.uuid)
    }

    recipient(room?: sdk.Room) {
        let members = this.members(room)?.map(m => {
            if (this.auth.profiles.get(this.sliceId(m.userId))){
                return `${this.auth.profiles.get(this.sliceId(m.userId))?.firstName} ${this.auth.profiles.get(this.sliceId(m.userId))?.lastName}` 
            }
            else return this.sliceId(m.name)
        })
        return members
    }
    getLastMessage(room: Room) {
        try {
            const messages = this.roomMessages?.get(room.roomId)?.filter((msg) => msg.event.getType() === EventType.RoomMessage)
            return messages ? messages[0] : null;
        } catch (err) {
            return null;
        }
    }
    lastMsg(room: Room) {
        const lastMessage = this.getLastMessage(room);
        return lastMessage ? lastMessage.event : null;
    }
    lastMsgTime(room: Room) {
        const lastMessage = this.getLastMessage(room);
        return lastMessage ? lastMessage.time : null;
    }
    lastMsgString(room: Room) {
        const lastMessage = this.getLastMessage(room);
        return lastMessage && lastMessage.event.event.content
            ? lastMessage.event.event.content['body']
            : 'No Messages Yet';
    }

    unread(room: Room) {
        return room.getRoomUnreadNotificationCount();
    }

    /**
     * Sorts an array of rooms by the time of their last message (descending).
     */
    timeSort(rooms: Room[]): Room[] {
        // return rooms
        try {
            return rooms.sort((a, b) => {
                // lastMsgTime might be undefined, so we use optional chaining if needed:
                return this.lastMsgTime(b)?.getTime() - this.lastMsgTime(a)?.getTime();
            });
        } catch (err) {
            // If something goes wrong, just return them unsorted:
            return rooms;
        }
    }

    getPFP(mem: RoomMember): string {
        let avatar = mem.user?.avatarUrl;
        let name = mem.name || mem.userId;

        if (avatar?.startsWith('mxc://')) {
            avatar = this.client?.mxcUrlToHttp(avatar, 50, 50, 'scale', true);
        }

        if (!avatar) {
            avatar = this.getProfile(mem).profile;
        }

        if (!avatar) {
            const authProfile = this.auth.profiles.get(this.sliceId(mem.userId));
            if (authProfile) {
                avatar = authProfile.pfp;
                name = `${authProfile.firstName} ${authProfile.lastName}`;
            }
        }

        if (!avatar) {
            // avatar = DEFAULT_PFP
            avatar = this.letterPFP(name);
        }

        return avatar;
    }
    color(userId: string): string {
        let rng = gen.create(userId);
        return new Color({ r: rng(255), g: rng(255), b: rng(255) }).hex
    }

    letterPFP(name: string): string {
        return `https://dummyimage.com/50x50/${this.color(name).slice(1)}/ffffff&text=${name.slice(0, 1)}`;
    }

    async addReaction({ roomId, messageId, emoji }: { roomId: string, messageId: string, emoji: string }) {
        this.client.sendEvent(roomId, EventType.Reaction, {
            'm.relates_to': {
                event_id: messageId,
                key: emoji,
                rel_type: "m.annotation",
            },

        }).then((response) => {
            console.log('REACTION RESPONSE', response);

        })
    }

    async redactEvent(event: MatrixEvent) {
        if (!event.event.room_id || !event.event.event_id) return;
        this.client.redactEvent(event.event.room_id, event.event.event_id, event.event.txn_id);
    }

    async acceptInvite(roomId: string): Promise<boolean> {
        return new Promise<boolean>(async (resolve, reject) => {
            try {
                await this.client.joinRoom(roomId);
                console.log(`Joined room: ${roomId}`);
                this.refreshRooms()
                resolve(true);
            } catch (error) {
                console.error(`Error joining room: ${roomId}`, error);
                reject(error);
            }
        });
    }


    async leaveRoom(roomId: string): Promise<boolean> {
        return new Promise<boolean>(async (resolve, reject) => {
            const alert = await this.alert.create({
                cssClass: 'my-custom-class',
                header: 'Confirm Leave Chat',
                message: `Are you sure you want to leave this chat? You will need to be re-invited to rejoin.`,
                buttons: [
                    {
                        text: 'Leave Chat',
                        handler: async () => {
                            try {
                                await this.client.leave(roomId);
                                console.log(`Declined invite/Left room: ${roomId}`);
                                // this.menu.close()
                                this.refreshRooms()
                                this.nav.navigateBack(['app', 'tabs', 'chat'])
                                resolve(true);
                            } catch (error) {
                                console.error(`Error leaving room: ${roomId}`, error);
                                reject(error);
                            }
                        }
                    },
                    {
                        text: 'Nevermind',
                        role: 'cancel',
                    }
                ]
            });
            await alert.present();
        });
    }

    /**
     * Return the user's avatar color / profile pic from matrix
     */
    getProfile(user: RoomMember) {
        let profile = this.profiles.get(user.userId);

        if (profile) {
            return profile;
        }
        
        // if (this.auth.getProfile[this.sliceId(user.userId)]) {
        //     profile = this.auth.profiles[this.sliceId(user.userId)];
        //     console.log("Profile from auth:", profile);
            
        //     // this.profiles.set(user.userId, profile);
        //     // return profile;
        // }

        // e.g. fetch an avatar URL at size 150
        const url = user.getAvatarUrl(MATRIX_URL, 150, 150, "scale", true, true);
        const rng = gen.create(user.userId);
        profile = {
            profile: url || undefined,
            color: new Color({ r: rng(255), g: rng(255), b: rng(255) }).hex
        };
        this.profiles.set(user.userId, profile);
        return profile;
    }

    getRoomAvatar(room: Room): string | null {
        let avatar = room.getAvatarUrl(MATRIX_URL, 150, 150, "scale", true);
        return avatar;
    }

    memberDisplayName(member: RoomMember) {
        if (this.auth.profiles.get(this.sliceId(member.userId))) {
            return `${this.auth.profiles.get(this.sliceId(member.userId))?.firstName} ${this.auth.profiles.get(this.sliceId(member.userId))?.lastName}`;
        }
        return member.name || member.userId;
    }

    idDisplayName(id: string) {
        const mem = {
            userId: id,
            name: id
        }
        return this.memberDisplayName(mem as RoomMember);
    }

    /**
     * Create a direct message room with a user, optionally sending an initial message.
     * This now checks for an existing direct chat using findDMRoomForUser.
     */
    async createRoom(otherNocoId: string | number, message?: string): Promise<string> {
        if (!this.clientReady) await this.init(this.auth.appAuth);
        if (!this.client) throw new Error("Matrix client not ready");

        return new Promise<string>(async (resolve, reject) => {
            // Convert IDs to string to form the localpart
            const toMatrixId = `@${otherNocoId}:${MATRIX}`;
            // const fromNocoId = this.auth.user?.uuid;
            // if (!fromNocoId) {
            //     return reject("No current user ID to create room with");
            // }

            // 1) Check if a DM with this user already exists
            const existingRoomId = this.findDMRoomForUser(toMatrixId);
            console.log("Existing DM room:", existingRoomId);
            
            if (existingRoomId) {
                await this.refreshRooms();
                if (message && message.length > 0) {
                    await this.sendMessage(existingRoomId, message);
                }
                return resolve(this.sliceId(existingRoomId));
            }

            // 2) Otherwise, create a new direct room
            try {
                console.log("Creating new direct message room with:", toMatrixId);
                const response = await this.client.createRoom({
                    invite: [toMatrixId],
                    visibility: Visibility.Private,
                    preset: Preset.TrustedPrivateChat,
                    is_direct: true
                });
                // Optionally send an initial message
                if (message && message.length > 0) {
                    await this.sendMessage(response.room_id, message);
                }
                await this.refreshRooms();
                resolve(this.sliceId(response.room_id));
            } catch (error: any) {
                console.log("Error creating room:", error.message, error.errcode);
                // handle known errors or re-throw
                if (error.message?.includes("room already exists")) {
                    console.log("Room already exists, refreshing");
                    
                    // ...
                }
                if (error.message?.includes("is already in the room")) {
                    console.log("User already in room, refreshing");
                    // ...
                    reject("Already created direct message room, refreshing");
                }
                reject(error);
            }
        });
    }

    /**
     * Finds an existing DM room ID with the given user, if it exists.
     * 1) Checks m.direct account data first,
     * 2) Fallback to scanning your joined rooms for a 2-member direct chat.
     */
    findDMRoomForUser(otherUserId: string): string | null {
        if (!this.client) throw new Error("Matrix client not ready");

        // 2) Fallback: check all rooms to see if there's exactly 2 joined members: me + otherUserId
        const allRooms = this.client.getRooms();
        for (const room of allRooms) {
            const members = room.getJoinedMembers().map(m => m.userId);
            if (
                members.includes(this.client.getUserId()!) &&
                members.includes(otherUserId) &&
                members.length === 2
            ) {
                return room.roomId;
            }
        }
        return null;
    }

    /**
     * Check if a room with a given "roomName" exists via matrix search.
     * (Kept for compatibility, though we no longer rely on this for 1:1 DMs.)
     */
    checkRoomExists(roomName: string): Promise<IEventWithRoomId | undefined> {
        return new Promise(async (resolve) => {
            if (!this.client) return resolve(undefined);

            const searchResults = await this.client.search({
                body: {
                    search_categories: {
                        room_events: {
                            search_term: roomName,
                            filter: {
                                types: ["m.room.name"]
                            }
                        }
                    }
                }
            });

            if (
                searchResults.search_categories.room_events.results &&
                searchResults.search_categories.room_events.results.length
            ) {
                resolve(
                    searchResults.search_categories.room_events.results[0]
                        .result as IEventWithRoomId
                );
            } else {
                resolve(undefined);
            }
        });
    }

    /**
     * Send a text message to a given room
     */
    async sendMessage(roomId: string, message: string): Promise<string> {
        if (!this.client) throw new Error("Matrix client not ready");
        console.log("Sending message to room:", roomId, message);
        return new Promise<string>((resolve, reject) => {
            this.client!
                .sendTextMessage(roomId, message)
                .then((res: any) => {
                    console.log("Message sent successfully");
                    resolve(res);
                })
                .catch((error: MatrixError) => {
                    console.log("Error sending message:", error.message);
                    reject(error);
                });
        });
    }

    /**
     * Show a local toast notification for new messages or invites
     */
    async notify(data: ToastData) {
        const buttons: any[] = [];
        buttons.push({ side: "start", icon: "chatbubble-ellipses" });

        if (data.linkText?.toLowerCase() === "nav" && data.link) {
            buttons.push({
                side: "end",
                text: "VIEW",
                handler: async () => {
                    this.nav.navigateForward(data.link!);
                }
            });
        }
        if (data.roomId) {
            buttons.push({
                side: "end",
                text: data.linkText ? data.linkText : "VIEW",
                handler: () => {
                    this.nav.navigateForward(["/app", "tabs", "chat", "room", data.roomId]);
                }
            });
        }

        this.system.vibrate();
        const toast = await this.toast.create({
            header: data.header,
            message: data.message,
            duration: data.duration ? data.duration : 5000,
            position: "top",
            color: data.color ? data.color : "lightblue",
            cssClass: "my-notify",
            buttons
        });
        toast.present();
        return toast;
    }

    /**
     * Open a modal to pick a user to message
     */
    async openNewMessage(incomingSearch?: string) {
        const modal = await this.modal.create({
            component: NewMessageComponent,
            cssClass: "main-modal",
            handle: true,
            initialBreakpoint: 0.95,
            breakpoints: [0, 0.5, 0.95],
            componentProps: incomingSearch ? { incomingSearch } : {}
        });

        modal.onDidDismiss().then((res: any) => {
            if (res && res.data && res.data.length > 0) {
                setTimeout(() => {
                    this.nav.navigateForward([
                        "/app",
                        "tabs",
                        "chat",
                        "room",
                        res.data.split(":")[0]
                    ]);
                }, 500);
            }
        });
        return await modal.present();
    }

    /**
     * Utility to remove the domain portion from a roomId. E.g.:
     * "!AbCdEfG:matrix.yourserver.com" => "!AbCdEfG"
     */
    // sliceRoomId(room_id: string) {
    //     const colonIndex = room_id.indexOf(":");
    //     if (colonIndex > 0) {
    //         return room_id.slice(0, colonIndex);
    //     }
    //     return room_id;
    // }
    sliceId(id: string){
        if (id.startsWith('@') || id.startsWith('!') || id.startsWith('#')){
            id = id.slice(1)
        }
        const colon = id.indexOf(':')
        if (colon < 0) return id
        else return id.slice(0, colon)
    }

    /**
     * Delay helper
     */
    delay(ms: number) {
        return new Promise((resolve) => {
            setTimeout(resolve, ms);
        });
    }
}